// Which branches of a project are folded away — per person, per project.
// The project page holds one and hands it to the map, Outline and Gantt
// chart, so folding a branch in one folds it in all of them.
import { useCallback, useEffect, useState } from 'react';
import { TaskItem } from '../../types/tasks';

const keyFor = (projectId: number) => `project.${projectId}.collapsed`;

function read(projectId: number): Set<number> {
  try {
    return new Set(JSON.parse(localStorage.getItem(keyFor(projectId)) || '[]'));
  } catch {
    return new Set();
  }
}

export function useCollapsed(projectId: number, items: TaskItem[]) {
  const [collapsed, setCollapsed] = useState<Set<number>>(() => read(projectId));
  // Another project opened on the same page.
  useEffect(() => setCollapsed(read(projectId)), [projectId]);

  const save = useCallback(
    (next: Set<number>) => {
      setCollapsed(next);
      try {
        localStorage.setItem(keyFor(projectId), JSON.stringify(Array.from(next)));
      } catch {
        /* per-viewer convenience only */
      }
    },
    [projectId]
  );

  const toggle = useCallback(
    (item: TaskItem) => {
      const next = new Set(collapsed);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      save(next);
    },
    [collapsed, save]
  );

  /** Unfolds everything above an item so it can be seen. */
  const expandTo = useCallback(
    (item: TaskItem) => {
      const byId = new Map(items.map((i) => [i.id, i]));
      const next = new Set(collapsed);
      let changed = false;
      for (let p = item.parent_id ? byId.get(item.parent_id) : undefined; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) {
        if (next.delete(p.id)) changed = true;
      }
      if (changed) save(next);
    },
    [items, collapsed, save]
  );

  /** Topics with sub-topics: what "Collapse all" folds. */
  const parentIds = useCallback(() => {
    const ids = new Set(items.map((i) => i.id));
    return new Set(items.filter((i) => i.parent_id !== null && ids.has(i.parent_id)).map((i) => i.parent_id as number));
  }, [items]);

  const expandAll = useCallback(() => save(new Set()), [save]);
  /** Folds every topic that has sub-topics, leaving just the main branches. */
  const collapseAll = useCallback(() => save(parentIds()), [save, parentIds]);
  const allCollapsed = (() => {
    const ids = parentIds();
    return ids.size > 0 && Array.from(ids).every((id) => collapsed.has(id));
  })();
  const anyCollapsed = (() => {
    const ids = parentIds();
    return Array.from(collapsed).some((id) => ids.has(id));
  })();

  return { collapsed, toggle, expandTo, expandAll, collapseAll, allCollapsed, anyCollapsed };
}

export type Folding = ReturnType<typeof useCollapsed>;
